import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Chip,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Store as StoreIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Person as PersonIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { Pie } from 'react-chartjs-2';

const LocationStats = ({ data, selectedMonth }) => {
  const { sales, products } = data;
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [locationStats, setLocationStats] = useState([]);
  const [chartData, setChartData] = useState(null);
  
  // Lista lokalizacji
  const locations = [
    { id: 'PAN', name: 'Panorama M&M GOLD', hours: '11:00-20:00' },
    { id: 'VVS', name: 'Panorama VVS', hours: '11:00-20:00' },
    { id: 'MODO', name: 'Modo M&M GOLD', hours: '10:00-21:00' },
    { id: 'KLIF', name: 'Klif M&M GOLD', hours: '09:00-21:00' },
    { id: 'GP', name: 'Galeria Północna M&M GOLD', hours: '10:00-22:00' },
    { id: 'SB', name: 'Stary Browar M&M GOLD', hours: '09:00-21:00' },
    { id: 'MB', name: 'Marbella', hours: '' }
  ];

  // Przygotowanie danych statystycznych dla lokalizacji
  useEffect(() => {
    if (!sales || !products) return;
    
    // Obliczenie całkowitej sprzedaży i liczby produktów
    const totalSalesAmount = sales.reduce((sum, sale) => sum + (parseFloat(sale.totalAmount) || 0), 0);
    const totalProductsCount = products.length;
    
    // Pobranie danych z poprzedniego miesiąca (dla trendów)
    const currentMonth = selectedMonth;
    const previousMonth = new Date(currentMonth);
    previousMonth.setMonth(previousMonth.getMonth() - 1);
    
    // Filtrowanie sprzedaży z poprzedniego miesiąca
    const previousMonthSales = data.sales?.filter(sale => {
      let saleDate;
      if (sale.createdAt && typeof sale.createdAt.toDate === 'function') {
        saleDate = sale.createdAt.toDate();
      } else if (sale.createdAt) {
        saleDate = new Date(sale.createdAt);
      }

      // Sprawdź, czy data jest prawidłowa przed użyciem
      if (saleDate && !isNaN(saleDate.getTime())) {
        return saleDate.getMonth() === previousMonth.getMonth() && 
               saleDate.getFullYear() === previousMonth.getFullYear();
      }
      return false;
    }) || [];
    
    // Przygotowanie statystyk dla każdej lokalizacji
    const stats = locations.map(location => {
      // Filtrowanie sprzedaży dla danej lokalizacji w bieżącym miesiącu
      const locationSales = sales.filter(sale => 
        sale.productData?.location === location.id
      );
      
      // Filtrowanie sprzedaży dla danej lokalizacji w poprzednim miesiącu
      const locationPrevSales = previousMonthSales.filter(sale => 
        sale.productData?.location === location.id
      );
      
      // Filtrowanie produktów dla danej lokalizacji
      const locationProducts = products.filter(product => 
        product.location === location.id
      );
      
      // Obliczanie statystyk bieżącego miesiąca
      const totalSales = locationSales.length;
      const totalAmount = locationSales.reduce((sum, sale) => sum + (parseFloat(sale.totalAmount) || 0), 0);
      const avgTicket = totalSales > 0 ? totalAmount / totalSales : 0;
      const productsCount = locationProducts.length;
      const reservedProducts = locationProducts.filter(p => p.status === 'reserved').length;
      
      // Obliczanie statystyk poprzedniego miesiąca
      const prevTotalAmount = locationPrevSales.reduce((sum, sale) => sum + (parseFloat(sale.totalAmount) || 0), 0);
      
      // Obliczanie realnych procentów
      const salesPercentage = totalSalesAmount > 0 ? Math.round((totalAmount / totalSalesAmount) * 100) : 0;
      const productsPercentage = totalProductsCount > 0 ? Math.round((productsCount / totalProductsCount) * 100) : 0;
      
      // Obliczanie trendu sprzedaży względem poprzedniego miesiąca
      let salesTrend = 0;
      if (prevTotalAmount > 0) {
        // Procentowa zmiana względem poprzedniego miesiąca
        salesTrend = Math.round(((totalAmount - prevTotalAmount) / prevTotalAmount) * 100);
      } else if (totalAmount > 0) {
        // Jeśli w poprzednim miesiącu nie było sprzedaży, a teraz jest
        salesTrend = 100; // 100% wzrost (nowa sprzedaż)
      }
      
      return {
        ...location,
        totalSales,
        totalAmount,
        avgTicket,
        productsCount,
        reservedProducts,
        salesTrend,
        salesPercentage,
        productsPercentage,
        prevTotalAmount // Dodajemy dla referencji
      };
    });
    
    setLocationStats(stats);
    
    // Przygotowanie danych do wykresu
    prepareChartData(stats);
    
  }, [sales, products, selectedMonth]);
  
  // Przygotowanie danych do wykresu
  const prepareChartData = (stats) => {
    const chartData = {
      labels: stats.map(loc => loc.name),
      datasets: [
        {
          data: stats.map(loc => loc.totalAmount),
          backgroundColor: [
            '#3f51b5', // primary
            '#f50057', // secondary
            '#4caf50', // success
            '#ff9800', // warning
            '#2196f3', // info
            '#9c27b0', // purple
            '#e91e63'  // pink
          ],
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverOffset: 10
        }
      ]
    };
    
    setChartData(chartData);
  };
  
  // Obsługa zmiany wybranej lokalizacji
  const handleLocationChange = (event) => {
    setSelectedLocation(event.target.value);
  };
  
  // Formatowanie waluty
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
      minimumFractionDigits: 2
    }).format(value);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Statystyki lokalizacji - {selectedMonth && !isNaN(new Date(selectedMonth).getTime()) ? `Dane za ${format(new Date(selectedMonth), 'LLLL yyyy', { locale: pl })}` : 'Ładowanie danych...'}
      </Typography>
      
      <Grid container spacing={3}>
        {/* Wykres porównawczy lokalizacji */}
        <Grid item xs={12} md={5}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardHeader 
              title="Porównanie sprzedaży w lokalizacjach" 
              avatar={<StoreIcon color="primary" />}
            />
            <CardContent sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {chartData ? (
                <Pie 
                  data={chartData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: {
                          boxWidth: 15,
                          padding: 15,
                          font: {
                            size: 12
                          }
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) => {
                            const value = context.raw;
                            return `${context.label}: ${formatCurrency(value)}`;
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <Typography>Brak danych do wyświetlenia</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Tabela z podsumowaniem lokalizacji */}
        <Grid item xs={12} md={7}>
          <Card elevation={3}>
            <CardHeader 
              title="Podsumowanie lokalizacji" 
              action={
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="location-select-label">Lokalizacja</InputLabel>
                  <Select
                    labelId="location-select-label"
                    id="location-select"
                    value={selectedLocation}
                    label="Lokalizacja"
                    onChange={handleLocationChange}
                  >
                    <MenuItem value="all">Wszystkie</MenuItem>
                    {locations.map(loc => (
                      <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              }
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Lokalizacja</TableCell>
                      <TableCell>Obrót</TableCell>
                      <TableCell>Liczba sprzedaży</TableCell>
                      <TableCell>Produkty</TableCell>
                      <TableCell>Udział w sprzedaży</TableCell>
                      <TableCell>Trend</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locationStats
                      .filter(loc => selectedLocation === 'all' || loc.id === selectedLocation)
                      .map((loc) => (
                        <TableRow key={loc.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                                <StoreIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {loc.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {loc.hours}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(loc.totalAmount)}</TableCell>
                          <TableCell align="right">{loc.totalSales}</TableCell>
                          <TableCell align="right">{loc.productsCount}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2">
                                {loc.salesPercentage}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                              {loc.salesTrend > 0 ? (
                                <TrendingUpIcon sx={{ color: 'success.main', fontSize: '1rem', mr: 0.5 }} />
                              ) : loc.salesTrend < 0 ? (
                                <TrendingDownIcon sx={{ color: 'error.main', fontSize: '1rem', mr: 0.5 }} />
                              ) : (
                                <span style={{ width: '1rem', marginRight: '0.5rem' }}></span>
                              )}
                              <Typography 
                                variant="body2" 
                                color={loc.salesTrend > 0 ? 'success.main' : loc.salesTrend < 0 ? 'error.main' : 'text.secondary'}
                              >
                                {loc.salesTrend > 0 ? '+' : ''}{loc.salesTrend}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    {locationStats.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" sx={{ py: 2 }}>
                            Brak danych dla lokalizacji
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Szczegółowe statystyki dla wybranej lokalizacji */}
        {selectedLocation !== 'all' && (
          <Grid item xs={12}>
            <Card elevation={3}>
              <CardHeader 
                title={`Szczegółowe statystyki - ${locationStats.find(loc => loc.id === selectedLocation)?.name || ''}`}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Obrót miesięczny
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <MoneyIcon sx={{ mr: 1, color: 'success.main' }} />
                        <Typography variant="h6">
                          {formatCurrency(locationStats.find(loc => loc.id === selectedLocation)?.totalAmount || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Liczba sprzedaży
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, color: 'info.main' }} />
                        <Typography variant="h6">
                          {locationStats.find(loc => loc.id === selectedLocation)?.totalSales || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Produkty w lokalizacji
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <InventoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">
                          {locationStats.find(loc => loc.id === selectedLocation)?.productsCount || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Udział w całkowitej sprzedaży
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" fontWeight="bold">
                          {locationStats.find(loc => loc.id === selectedLocation)?.salesPercentage || 0}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={locationStats.find(loc => loc.id === selectedLocation)?.salesPercentage || 0} 
                        color="primary"
                        sx={{ height: 10, borderRadius: 5 }}
                      />
                    </Box>
                    
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Udział w całkowitej liczbie produktów
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" fontWeight="bold">
                          {locationStats.find(loc => loc.id === selectedLocation)?.productsPercentage || 0}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={locationStats.find(loc => loc.id === selectedLocation)?.productsPercentage || 0} 
                        color="secondary"
                        sx={{ height: 10, borderRadius: 5 }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default LocationStats;
